/**
 * Section: Hero Banner / Slideshow JS
 * Danish Modern L.A. Shopify Theme
 */

if (!customElements.get('slideshow-component')) {
  class SlideshowComponent extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('[data-slideshow]');
      if (!this.slider) return;

      this.track = this.querySelector('[data-slideshow-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.prevButton = this.querySelector('[data-slide-prev]');
      this.nextButton = this.querySelector('[data-slide-next]');
      this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));

      this.currentIndex = 0;
      this.autoplayTimer = null;

      this.autorotate = this.slider.dataset.autorotate === 'true';
      this.speed = parseInt(this.slider.dataset.speed, 10) || 5000;

      this.init();
    }

    init() {
      if (this.slides.length === 0) return;

      // Event Listeners for Navigation Arrows
      if (this.prevButton) {
        this.prevButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.prevSlide();
          this.resetAutoplay();
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.nextSlide();
          this.resetAutoplay();
        });
      }

      // Event Listeners for Pagination Dots
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToSlide(index);
          this.resetAutoplay();
        });
      });

      // Pause on Hover
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());

      // Touch Swipe Support for Mobile
      this.initTouch();

      // Click Redirection for Entire Slide
      this.slides.forEach((slide) => {
        const link = slide.dataset.slideLink;
        if (link) {
          slide.classList.add('slideshow__slide--clickable');
          slide.addEventListener('click', (e) => {
            if (!e.target.closest('a, button')) {
              window.location.href = link;
            }
          });
        }
      });

      // Initial state
      this.goToSlide(0);
      this.startAutoplay();
    }

    goToSlide(index) {
      if (index < 0) index = this.slides.length - 1;
      if (index >= this.slides.length) index = 0;

      // Pause previous video if present
      const currentSlide = this.slides[this.currentIndex];
      if (currentSlide) {
        const prevVideo = currentSlide.querySelector('video');
        if (prevVideo) prevVideo.pause();
      }

      this.currentIndex = index;

      // Translate track horizontally right-to-left
      if (this.track) {
        const translatePercentage = -100 * this.currentIndex;
        this.track.style.transform = `translate3d(${translatePercentage}%, 0, 0)`;
      }

      // Update slide active states & video playback
      this.slides.forEach((slide, i) => {
        const isActive = i === this.currentIndex;
        slide.classList.toggle('is-active', isActive);

        if (isActive) {
          const video = slide.querySelector('video');
          if (video && slide.dataset.autoplayVideo === 'true') {
            video.currentTime = 0;
            video.play().catch(() => {});
          }
        }
      });

      // Update dots navigation state
      this.dots.forEach((dot, i) => {
        const isActive = i === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    nextSlide() {
      this.goToSlide(this.currentIndex + 1);
    }

    prevSlide() {
      this.goToSlide(this.currentIndex - 1);
    }

    startAutoplay() {
      if (!this.autorotate || this.slides.length <= 1) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => this.nextSlide(), this.speed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resetAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    initTouch() {
      let touchStartX = 0;
      let touchEndX = 0;

      this.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) this.nextSlide();
          else this.prevSlide();
          this.resetAutoplay();
        }
      }, { passive: true });
    }
  }

  customElements.define('slideshow-component', SlideshowComponent);
}
